import axios from 'axios'
import './axiosConfig' // Ensures interceptors are registered
import {
  fromGatewayEnvelope,
  toGatewayError,
  type GatewayError,
  type GatewayResult,
} from '@/utils/gatewayError'
import { downloadBlob } from './blobDownload'
import { CONNECTED_METERS_ERROR_FALLBACK } from '@/utils/connectedMetersState'
import { debugLog } from '@/utils/debugLog'
import { collectValueNames } from '@/utils/codeName'

const API_BASE_URL = import.meta.env.VITE_API_URL

// API Response Types
export type ConsumerConsumptionData = {
  day: string
  consumption: number
  startReading: number
  microSpheres: number
}

// New API consumption data type
export type ApiConsumerConsumption = {
  day: string
  consumption: number
  startReading: number
  accuracyScore: number
}

// Daily consumption from direct endpoint (has day/night split)
export type ApiDailyConsumption = {
  day: string
  dailyConsumption: number
  nightlyConsumption: number
}

export type ConsumerData = {
  premiseId: number
  parentId?: number | null
  supplierOfId?: number | null
  siteId: number
  locationId: string
  billingId: number
  coordinates: string | null
  itmCoordinates?: { x: number; y: number } | null
  isSiteMain: boolean
  address: string
  usageType: string
  occupantCount: number
  payerId: number
  payerName: string
  initialPeriod: string
  date: string
  meterReading: number
  meterDistributionName: string | null
  meterModelId: number
  meterModel: string
  meterId: number
  sensorStatus: boolean
  status: string
  phoneNumber: string
  emails: string[]
  diameter: number
  rValue: number
  // Additional fields from API
  installedPeriod: string[]
  meterNumber: number
  meterDiscriminator: string | null
  meterNotes: string
  meterOpsNotes: string
  amrSystemKey: string
  amrId: string
  amrModelId: number
  amrIsActive: boolean
  lastValue: number | null
  lastReadingAt: string | null
  lastConsumption: number | null
  splitMeterConsumptionData: ConsumerConsumptionData[]
  apiConsumerConsumptions?: ApiConsumerConsumption[]
  apiDailyConsumptions?: ApiDailyConsumption[]
  parentCard?: string | number
  resources?: Array<{
    key?: string
    name: string
    value: any
    valueName?: string | null
  }>
}

export type ConsumersApiResponse = {
  success: boolean
  data: {
    total: number
    pageSize: number
    page: number
    result: ConsumerData[]
  }
}

// Transformed row type for DataGrid (dynamic — fields come from resources array)
export type ConsumerRow = {
  id: number | string
  [key: string]: any
}

// Fields to skip when flattening (not useful as table columns)
const SKIP_FIELDS = new Set([
  'splitMeterConsumptionData',
  'apiConsumerConsumptions',
  'apiDailyConsumptions',
  'resources',
  'meterModelId',
])

// Hebrew translations for known field names
export const HEBREW_HEADERS: Record<string, string> = {
  meterNumber: "מס' מד",
  meter_number: "מס' מד",
  payerName: 'שם הצרכן',
  payer_name: 'שם הצרכן',
  billingId: 'כרטיס מים',
  billing_id: 'כרטיס מים',
  address: 'כתובת',
  dailyUsageData: 'צ. אחרונות',
  daily_usage_data: 'צ. אחרונות',
  nightConsumption: 'צריכת לילה',
  night_consumption: 'צריכת לילה',
  consumptionNight: 'צריכת לילה',
  demandNight: 'צריכת לילה',
  avgWeeklyConsumption: 'ממוצע צריכה שבוע אחרון',
  rValue: 'ערך R',
  r_value: 'ערך R',
  date: 'נקלט לאחרונה',
  diameter: 'קוטר',
  premiseId: 'מספר נכס',
  premise_id: 'מספר נכס',
  occupantCount: 'מספר נפשות',
  occupant_count: 'מספר נפשות',
  locationId: 'אזור',
  location_id: 'אזור',
  parentCard: 'כרטיס אב',
  parent_card: 'כרטיס אב',
  isSiteMain: 'אתר ראשי',
  is_site_main: 'אתר ראשי',
  payerId: 'מספר משלם',
  payer_id: 'מספר משלם',
  phoneNumber: 'מספר טלפון',
  phone_number: 'מספר טלפון',
  emails: 'אימייל',
  email: 'אימייל',
  crm: 'CRM',
  usageType: 'סוג שימוש',
  usage_type: 'סוג שימוש',
  meterModelId: 'דגם מונה',
  meter_model_id: 'דגם מונה',
  meterModelName: 'דגם מונה',
  meter_model_name: 'דגם מונה',
  meterModel: 'דגם',
  meter_model: 'דגם',
  meterId: 'מזהה מונה',
  meter_id: 'מזהה מונה',
  amrIsActive: 'מחובר לקר"מ',
  amr_is_active: 'מחובר לקר"מ',
  amrSystemKey: 'שרת קריאות',
  amr_system_key: 'שרת קריאות',
  amrId: 'מזהה AMR',
  amr_id: 'מזהה AMR',
  amrModelId: 'דגם AMR',
  amr_model_id: 'דגם AMR',
  installedPeriod: 'תאריך התקנה',
  installation_date: 'תאריך התקנה',
  internal_meter_number: 'מספר מד פנימי',
  installed_period: 'תאריך התקנה',
  siteId: 'מספר אתר',
  site_id: 'מספר אתר',
  coordinates: 'קואורדינטות',
  itmCoordinates: 'קורדינטות לפי רשת ישראל החדשה',
  sensorStatus: 'סטטוס חיישן',
  sensor_status: 'סטטוס חיישן',
  status: 'סטטוס',
  meterReading: 'קריאת מונה',
  meter_reading: 'קריאת מונה',
  meterDistributionName: 'שם הפצה',
  meter_distribution_name: 'שם הפצה',
  meterDiscriminator: 'מבדיל מונה',
  meter_discriminator: 'מבדיל מונה',
  meterNotes: 'הערות מונה',
  meter_notes: 'הערות מונה',
  meterOpsNotes: 'הערות תפעול',
  meter_ops_notes: 'הערות תפעול',
  initialPeriod: 'תקופה ראשונית',
  initial_period: 'תקופה ראשונית',
  lastValue: 'קריאה אחרונה',
  last_value: 'קריאה אחרונה',
  lastReadingAt: 'תאריך קליטה',
  last_reading_at: 'תאריך קליטה',
  lastConsumption: 'צריכה אחרונה',
  last_consumption: 'צריכה אחרונה',
}

// Strip curly braces from PostgreSQL array-style strings like "{value}"
const cleanValue = (val: any): any => {
  if (typeof val === 'string') {
    return val.replace(/^\{|\}$/g, '').trim()
  }
  if (Array.isArray(val)) {
    return val.map(v => (typeof v === 'string' ? v.replace(/^\{|\}$/g, '').trim() : v))
  }
  return val
}

const firstNonEmptyValue = (...values: any[]): any => values.find(value => {
  if (value === null || value === undefined) return false
  if (typeof value === 'string') return value.trim() !== ''
  return true
})

const hasMeaningfulValue = (value: any): boolean => firstNonEmptyValue(value) !== undefined

const joinNonEmptyValues = (...values: any[]): string | undefined => {
  const parts = values
    .map(value => cleanValue(value))
    .filter(value => value !== null && value !== undefined && String(value).trim() !== '')
    .map(value => String(value).trim())

  return parts.length > 0 ? parts.join(' ') : undefined
}

// One formatter for the sparkline dates. toLocaleDateString(locale, options)
// builds a new Intl.DateTimeFormat on every call; at 7 points x 17k rows that
// was ~3s of a full-table export. Same locale/options, same output ("30.06").
const sparklineDateFormat = new Intl.DateTimeFormat('he-IL', {
  day: '2-digit',
  month: '2-digit',
})

// Transform API data to table row format — include ALL flat fields + resources
export const transformConsumerToRow = (
  consumer: Partial<ConsumerData> & Record<string, unknown>
): ConsumerRow => {
  const source = consumer as ConsumerData & Record<string, any>
  const premiseId = source.premiseId ?? source.premise_id ?? source.id
  const row: ConsumerRow = { id: premiseId }

  // 1. Spread all flat fields from the consumer object
  for (const [key, value] of Object.entries(consumer)) {
    if (SKIP_FIELDS.has(key)) continue
    const cleanedValue = cleanValue(value ?? null)
    row[key] = cleanedValue

    // Water-tree endpoints in older environments return snake_case while the
    // shared consumers table uses camelCase column fields. Preserve both.
    const camelKey = key.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase())
    if (camelKey !== key && row[camelKey] == null) row[camelKey] = cleanedValue
  }

  // Keep the night-consumption column stable across API naming variants.
  // Some responses expose this value as snake_case or consumptionNight.
  const nightConsumption = source.nightConsumption
    ?? source.night_consumption
    ?? source.consumptionNight
    ?? source.demandNight
  if (nightConsumption !== undefined) {
    row.nightConsumption = cleanValue(nightConsumption ?? null)
  }

  // 2. Build chart data from consumption arrays (last 7 days, chronological)
  const rawConsumptions = consumer.apiDailyConsumptions?.map(d => ({
    value: d.dailyConsumption ?? (d as any).consumption,
    day: d.day,
  })) ?? consumer.apiConsumerConsumptions?.map(d => ({
    value: d.consumption,
    day: d.day,
  })) ?? consumer.splitMeterConsumptionData?.map(d => ({
    value: d.consumption,
    day: d.day,
  })) ?? []
  const chartData = rawConsumptions
    .sort((a, b) => new Date(a.day).getTime() - new Date(b.day).getTime())
    .slice(-7)
    .map(d => ({
      value: d.value,
      date: sparklineDateFormat.format(new Date(d.day)),
    }))
  row.dailyUsageData = chartData

  // 2b. Compute average daily consumption from chart data
  if (chartData.length > 0) {
    const sum = chartData.reduce((acc, d) => acc + (d.value ?? 0), 0)
    row.avgWeeklyConsumption = Math.round((sum / chartData.length) * 10) / 10
  }

  // 3. Overlay resources before creating table aliases. Water-tree consumer
  // responses keep values such as name/address/city inside this array.
  if (consumer.resources) {
    for (const resource of consumer.resources) {
      const resourceKey = resource.key || resource.name
      if (!resourceKey) continue

      const cleanedValue = cleanValue(resource.value ?? null)
      if (!hasMeaningfulValue(row[resourceKey])) row[resourceKey] = cleanedValue

      const camelKey = resourceKey.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase())
      if (camelKey !== resourceKey && !hasMeaningfulValue(row[camelKey])) row[camelKey] = cleanedValue
    }
  }

  // 3b. The names of the codes the resources carry. The value stays the code.
  const valueNames = collectValueNames(consumer.resources)
  if (valueNames) row.valueNames = { ...row.valueNames, ...valueNames }

  // 4. Compatibility aliases used by the Analytics consumer table. Prefer
  // explicit flat values, then fall back to the resource keys returned by the
  // backend. Empty strings must not block a real resource value.
  const payerParts = joinNonEmptyValues(
    row.payerFirstName ?? row.payer_first_name,
    row.payerFamilyName ?? row.payer_family_name
  )

  row.waterCard = firstNonEmptyValue(row.waterCard, row.billingId, row.billing_id)
  row.meterNumber = firstNonEmptyValue(row.meterNumber, row.meter_number, row.meterId, row.meter_id)
  row.name = firstNonEmptyValue(
    row.name,
    row.payerName,
    row.payer_name,
    row.contactName,
    row.contact_name,
    row.payerFullName,
    row.payer_full_name,
    payerParts,
    row.payerFamilyName,
    row.payer_family_name
  )
  row.address = firstNonEmptyValue(
    row.address,
    row.contactAddress,
    row.contact_address,
    row.propertyAddress,
    row.property_address,
    row.payerAddress,
    row.payer_address,
    row.propertyStreet,
    row.property_street,
    row.payerStreet,
    row.payer_street
  )
  row.payerNumber = firstNonEmptyValue(row.payerNumber, row.payerId, row.payer_id)
  row.area = firstNonEmptyValue(
    row.area,
    row.locationId,
    row.location_id,
    row.contactCity,
    row.contact_city,
    row.payerCityName,
    row.payer_city_name,
    row.payerCity,
    row.payer_city,
    row.propertyCity,
    row.property_city
  )
  row.capacity = firstNonEmptyValue(row.capacity, row.lastValue, row.last_value, row.meterReading, row.meter_reading)
  row.contractNumber = firstNonEmptyValue(
    row.contractNumber,
    row.contract_number,
    row.contractId,
    row.contract_id,
    row.contractNo,
    row.contract_no,
    row.agreementNumber,
    row.agreement_number
  )
  row.tenantCount = firstNonEmptyValue(
    row.tenantCount,
    row.tenant_count,
    row.occupantCount,
    row.occupant_count
  )
  row.installed = firstNonEmptyValue(
    row.installed,
    Array.isArray(row.installedPeriod) ? row.installedPeriod[0] : row.installedPeriod,
    Array.isArray(row.installed_period) ? row.installed_period[0] : row.installed_period
  )
  row.installDate = firstNonEmptyValue(row.installDate, row.install_date, row.installed)
  row.phone = firstNonEmptyValue(row.phone, row.phoneNumber, row.phone_number, row.mobileNumber, row.mobile_number)
  row.email = firstNonEmptyValue(
    row.email,
    Array.isArray(row.emails) ? row.emails[0] : row.emails
  )

  return row
}

export type GetConsumersParams = {
  accountId: number | string
  page?: number
  size?: number
  sortBy?: string
  sortOrder?: 'ASC' | 'DESC'
  langKey?: string
}

export type ApiResourceFilter = {
  name: string
  value: string[]
  operator?: string
}

// Collect all unique field names across items: flat fields first, then dailyUsageData, then resource names
const collectAllFieldNames = (items: ConsumerData[]): string[] => {
  const seen = new Set<string>(['id']) // skip 'id' — it's always present in the row
  const names: string[] = []

  for (const item of items) {
    // Flat fields
    for (const key of Object.keys(item)) {
      if (SKIP_FIELDS.has(key) || seen.has(key)) continue
      seen.add(key)
      names.push(key)
    }
    // Computed chart field
    if (!seen.has('dailyUsageData')) {
      seen.add('dailyUsageData')
      names.push('dailyUsageData')
    }
    // Resource fields
    for (const r of item.resources ?? []) {
      if (r.key && !seen.has(r.key)) {
        seen.add(r.key)
        names.push(r.key)
      }
    }
  }
  return names
}

// Collect headers mapping (key -> localized name) from resources
export const collectResourceHeaders = (items: ConsumerData[]): Record<string, string> => {
  const headers: Record<string, string> = {}
  for (const item of items) {
    if (item.resources) {
      for (const res of item.resources) {
        if (res.key && res.name) {
          headers[res.key] = res.name
        }
      }
    }
  }
  return headers
}

// Some Gateway answers arrive double JSON-encoded.
const decodeGatewayBody = (body: unknown): unknown => {
  while (typeof body === 'string') {
    try {
      body = JSON.parse(body)
    } catch {
      break
    }
  }
  return body
}

export const getConsumers = async (
  params: GetConsumersParams,
  filters: ApiResourceFilter[] = [],
  signal?: AbortSignal
): Promise<{
  rows: ConsumerRow[]
  total: number
  page: number
  pageSize: number
  resourceNames: string[]
  resourceHeaders: Record<string, string>
}> => {
  const { accountId, page = 1, size = 10, sortBy = 'premiseId', sortOrder = 'ASC', langKey = 'he' } = params

  try {
    // Pass filter names through as-is (resource names match API names directly)
    const requestBody: { apiResources: ApiResourceFilter[]; logicalOperator: string } = { apiResources: filters, logicalOperator: 'AND' }
    const apiUrl = `${API_BASE_URL}/api/consumer/${accountId}?page=${page}&size=${size}&sortBy=${sortBy}&sortOrder=${sortOrder}&langKey=${langKey}`

    debugLog('=== CONSUMER API REQUEST ===')
    debugLog('URL:', apiUrl)
    debugLog('Request Body:', JSON.stringify(requestBody, null, 2))
    debugLog('Filters:', filters)

    const res = await axios.post<ConsumersApiResponse>(
      apiUrl,
      requestBody,
      {
        headers: { 'Content-Type': 'application/json' },
        signal,
      }
    )

    debugLog('=== CONSUMER API RESPONSE ===')
    debugLog('Status:', res.status)
    debugLog('Raw Response:', res.data)

    const responseBody: any = decodeGatewayBody(res.data)
    const data = responseBody?.data
    const resultArray = data?.result ?? (Array.isArray(data) ? data : [])
    const rows = resultArray?.map(transformConsumerToRow) ?? []

    debugLog('Parsed data.total:', data?.total)
    debugLog('Result array length:', resultArray.length)
    debugLog('First result (if any):', resultArray[0])
    debugLog('Transformed rows count:', rows.length)

    return {
      rows,
      total: data?.total ?? resultArray.length ?? 0,
      page: data?.page ?? 1,
      pageSize: data?.pageSize ?? size,
      resourceNames: collectAllFieldNames(resultArray),
      resourceHeaders: collectResourceHeaders(resultArray),
    }
  } catch (error: any) {
    // A cancelled search is not an empty page: the caller that cancelled it ignores it.
    if (axios.isCancel(error)) throw error
    console.error('=== CONSUMER API ERROR ===')
    console.error('[consumerService] Failed to fetch consumers:', error)
    console.error('Error response:', error?.response?.data)
    console.error('Error status:', error?.response?.status)
    // Return empty fallback data instead of crashing
    return {
      rows: [],
      total: 0,
      page: 1,
      pageSize: size || 10,
      resourceNames: [],
      resourceHeaders: {},
    }
  }
}

// The list fields transformConsumerToRow reads to fill a grid field besides the
// field itself: an empty address falls back to the other address columns, and a
// missing meter number to the meter id. The lean export asks for these with
// the visible fields, so a lean row derives the same cell as a full-list row.
// The weekly average is not derived here: the Gateway serves it, so the 7 daily
// points are never requested.
const DERIVED_FIELD_SOURCES: Record<string, readonly string[]> = {
  address: ['contact_address', 'payer_address', 'property_street', 'payer_street'],
  meterNumber: ['meterId'],
  // The usage type is a code the Gateway names; the names ride in valueNames.
  usage_type_id: ['valueNames'],
}

// The wire fields a lean export of `fields` must ask for, in sheet order, the
// derivation sources after the field that needs them.
export const exportRequestFields = (fields: string[]): string[] =>
  Array.from(
    new Set(fields.flatMap(f => [f, ...(DERIVED_FIELD_SOURCES[f] ?? [])]))
  )

// Unlike getConsumers, rejects on a failed request, so a failed export writes
// no file instead of an empty one.
export const getConsumerExportRows = async (
  params: GetConsumersParams,
  filters: ApiResourceFilter[],
  fields: string[]
): Promise<ConsumerRow[]> => {
  const {
    accountId,
    page = 1,
    size = 10,
    sortBy = 'premiseId',
    sortOrder = 'ASC',
    langKey = 'he',
  } = params
  const query = new URLSearchParams({
    page: String(page),
    size: String(size),
    sortBy,
    sortOrder,
    langKey,
    fields: exportRequestFields(fields).join(','),
  })
  const res = await axios.post(
    `${API_BASE_URL}/api/consumer/${accountId}?${query}`,
    { apiResources: filters, logicalOperator: 'AND' }
  )
  const body = decodeGatewayBody(res.data) as {
    data?: { fields?: string[]; rows?: unknown[][]; result?: ConsumerData[] }
  } | null
  const { fields: served, rows, result = [] } = body?.data ?? {}
  // A Gateway that predates the lean export ignores `fields` and answers the
  // full list.
  if (!served || !rows) return result.map(transformConsumerToRow)
  return rows.map(cells =>
    transformConsumerToRow(
      Object.fromEntries(served.map((field, i) => [field, cells[i]]))
    )
  )
}

// Fetch filter options for a specific field
export type FilterOptionsResponse = {
  success: boolean
  data: string[]
  message?: string
  error?: string | null
  statusCode?: number
}



// Helper to remove curly braces from API values like "{0502526556}"
const stripBraces = (value: string | null | undefined): string => {
  if (!value) return ''
  return value.replace(/^\{|\}$/g, '').trim()
}

export const getFilterOptions = async (
  accountId: number | string,
  field: string,
  langKey: string = 'he'
): Promise<string[]> => {
  try {
    const res = await axios.get<FilterOptionsResponse>(
      `${API_BASE_URL}/api/consumer/${accountId}/filters`,
      {
        params: { field, langKey },
      }
    )

    const rawData = res.data?.data ?? []

    // Clean up braces from array-like strings (e.g. "{email@example.com}")
    return rawData.map(item => stripBraces(item))
  } catch (error) {
    console.error(`[consumerService] Failed to fetch filter options for ${field}:`, error)
    return []
  }
}

export type FilterOption = { value: string; name: string | null }

// Fetch the options of a field with the name of each code. A Gateway without the
// route answers the plain list, so the options come back with no names.
export const getFilterOptionsWithNames = async (
  accountId: number | string,
  field: string,
  langKey: string = 'he'
): Promise<FilterOption[]> => {
  try {
    const res = await axios.get<{
      data?: Array<{ value: string; name?: string | null }>
    }>(`${API_BASE_URL}/api/consumer/${accountId}/filter-options`, {
      params: { field, langKey },
    })
    return (res.data?.data ?? []).map(option => ({
      value: stripBraces(option.value),
      name: option.name ?? null,
    }))
  } catch {
    const values = await getFilterOptions(accountId, field, langKey)
    return values.map(value => ({ value, name: null }))
  }
}

// Single consumer details response type
export type SingleConsumerResponse = {
  success: boolean
  data: ConsumerData
  message?: string
  error?: string | null
  statusCode?: number
}

export const CONSUMER_FETCH_FAILED = 'טעינת פרטי המד נכשלה'

export type ConsumerFetchResult = GatewayResult<ConsumerData>

// The Gateway answers {lat: 0, lan: 0} for a premise with no stored point.
const isUnplaced = (coordinates: unknown): boolean => {
  if (!coordinates || typeof coordinates !== 'object') return false
  const { lat, lan } = coordinates as { lat?: unknown; lan?: unknown }
  return Number(lat) === 0 && Number(lan) === 0
}

/**
 * Fetch one consumer by meter number and keep the reason a failure gives.
 * A consumer page that cannot load must say why; rendering it as empty data
 * turns a 400 or a 500 into rows of "-".
 */
export const fetchSingleConsumer = async (
  accountId: number | string,
  meterNumber: string | number,
  langKey: string = 'he'
): Promise<ConsumerFetchResult> => {
  try {
    const res = await axios.post<SingleConsumerResponse>(
      `${API_BASE_URL}/api/consumer/${accountId}/${meterNumber}?langKey=${langKey}`,
      {},
      {
        headers: { 'Content-Type': 'application/json' },
      }
    )

    // Handle double JSON-encoded responses
    let responseBody: any = res.data
    while (typeof responseBody === 'string') {
      try {
        responseBody = JSON.parse(responseBody)
      } catch {
        break
      }
    }

    if (responseBody?.success && responseBody?.data) {
      const data = responseBody.data
      return {
        ok: true,
        data: isUnplaced(data.coordinates)
          ? { ...data, coordinates: null }
          : data,
      }
    }

    return {
      ok: false,
      error: fromGatewayEnvelope(responseBody, CONSUMER_FETCH_FAILED),
    }
  } catch (error) {
    console.error(`[consumerService] Failed to fetch single consumer:`, error)
    return { ok: false, error: toGatewayError(error, CONSUMER_FETCH_FAILED) }
  }
}

// Fetch single consumer by meterNumber using direct endpoint
export const getSingleConsumer = async (
  accountId: number | string,
  meterNumber: string | number,
  langKey: string = 'he'
): Promise<ConsumerData | null> => {
  const result = await fetchSingleConsumer(accountId, meterNumber, langKey)
  return result.ok ? result.data : null
}

// Update consumer data type for API request
export type UpdateConsumerRequest = {
  premiseId: number
  siteId: number
  // A blank location must travel as null: the Gateway writes any non-null value
  // into an integer_path column, and '' fails that column's check constraint.
  locationId: string | null
  billingId: number
  // lat holds the longitude and lan the latitude; latitude and longitude say what they hold.
  coordinates: {
    lat: number
    lan: number
    latitude?: number
    longitude?: number
  } | null
  itmCoordinates: { x: number; y: number }
  isSiteMain: boolean
  address: string
  usageType: string
  occupantCount: number
  payerName: string
  phoneNumber: string | null
  emails: string[]
  installedPeriod: string[]
  meterNumber: number
  meterId: number
  meterDiscriminator: string | null
  meterModelId: number
  meterNotes: string | null
  meterOpsNotes: string | null
  amrSystemKey: string
  amrId: string
  amrModelId: number
  amrIsActive: boolean
  diameter: number
  rValue: number
  payerId: number
  sensorStatus: boolean
}

// Update consumer API response
export type UpdateConsumerResponse = {
  success: boolean
  message?: string
  error?: string | null
  code?: string
  statusCode?: number
}

export const CONSUMER_SAVE_FAILED = 'שמירת פרטי המד נכשלה'

export type UpdateConsumerResult =
  | { success: true; data?: any }
  | { success: false; error: GatewayError }

// Update a consumer's data
export const updateConsumer = async (
  accountId: number | string,
  meterNumber: string | number,
  data: Partial<UpdateConsumerRequest>
): Promise<UpdateConsumerResult> => {
  try {
    const res = await axios.put<UpdateConsumerResponse>(
      `${API_BASE_URL}/api/consumer/${accountId}/${meterNumber}`,
      data,
      {
        headers: { 'Content-Type': 'application/json' },
      }
    )

    if (res.data?.success) {
      return { success: true, data: res.data }
    }

    return {
      success: false,
      error: fromGatewayEnvelope(res.data, CONSUMER_SAVE_FAILED),
    }
  } catch (error: any) {
    console.error(`[consumerService] Failed to update consumer:`, error)
    return { success: false, error: toGatewayError(error, CONSUMER_SAVE_FAILED) }
  }
}

export const LOCATIONS_FETCH_FAILED = 'רשימת האזורים לא נטענה'

/** The site's active areas, by path and name. A failure keeps its reason: an empty list would leave the area row read-only with no hint. */
export const fetchLocations = async (
  accountId: number | string
): Promise<GatewayResult<Array<{ locationId: string; locationName: string }>>> => {
  try {
    const res = await axios.get(
      `${API_BASE_URL}/api/consumer/${accountId}/locations`
    )
    return {
      ok: true,
      data: Array.isArray(res.data?.data) ? res.data.data : [],
    }
  } catch (error) {
    console.error('[consumerService] Failed to load the areas:', error)
    return { ok: false, error: toGatewayError(error, LOCATIONS_FETCH_FAILED) }
  }
}

// Fetch connected meters (relationships)
export const getConnectedMeters = async (
  accountId: number | string,
  params: {
    page: number
    size: number
    premiseId: number | string
    parentId?: number | string
    from?: string
    to?: string
    langKey?: string
  }
): Promise<{
  rows: ConsumerRow[]
  total: number
  page: number
  pageSize: number
  resourceNames: string[]
  resourceHeaders: Record<string, string>
  error: GatewayError | null
}> => {
  const { page, size, premiseId, parentId, from, to, langKey = 'he' } = params

  // Construct query params
  const queryParams = new URLSearchParams()
  queryParams.append('page', page.toString())
  queryParams.append('size', size.toString())
  queryParams.append('premiseId', premiseId.toString())
  queryParams.append('parentId', (parentId ?? premiseId).toString()) // parentId is required
  if (from) queryParams.append('from', from)
  if (to) queryParams.append('to', to)
  queryParams.append('langKey', langKey)

  try {
    const requestBody = { apiResources: [] }

    const res = await axios.post<ConsumersApiResponse>(
      `${API_BASE_URL}/api/consumer/${accountId}/relationship?${queryParams.toString()}`,
      requestBody,
      {
        headers: { 'Content-Type': 'application/json' },
      }
    )

    const data = res.data?.data
    const resultArray = data?.result ?? []
    const rows = resultArray.map(transformConsumerToRow)

    return {
      rows,
      total: data?.total ?? rows.length,
      page: data?.page ?? page,
      pageSize: data?.pageSize ?? size,
      resourceNames: collectAllFieldNames(resultArray),
      resourceHeaders: collectResourceHeaders(resultArray),
      error: null,
    }
  } catch (error) {
    console.error('[consumerService] Failed to fetch connected meters:', error)
    return {
      rows: [],
      total: 0,
      page,
      pageSize: size,
      resourceNames: [],
      resourceHeaders: {},
      error: toGatewayError(error, CONNECTED_METERS_ERROR_FALLBACK),
    }
  }
}

// Convert to number or null (preserve empty/null instead of defaulting to 0)
const toNumOrNull = (v: any): number | null => {
  if (v === null || v === undefined || v === '') return null
  const n = Number(v)
  return isNaN(n) ? null : n
}

// Hourly readings types
export type HourlyReadingApiItem = {
  consumerId: number
  meterNumber: number | null
  date: string
  day: string | null
  consumption: number | null
  lastConsumption: number | null
  consumed: number
}

export type HourlyReadingsApiResponse = {
  success: boolean
  data: {
    collection: {
      total: number
      pageSize: number
      page: number
      result: HourlyReadingApiItem[]
    }
    totalConsumption: number
    avgConsumption: number
  }
}

// Transformed row type for DataGrid
export type HourlyReadingRow = {
  id: number
  meterNumber: string
  timestamp: string
  consumption: number | null
  value: number
  dayOfWeek: string
}

const getDayOfWeekLabel = (value: string | null | undefined) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'][date.getUTCDay()]
}

// Transform API data to DataGrid row format
const transformHourlyToRow = (item: HourlyReadingApiItem, fallbackMeterNumber: string, index: number): HourlyReadingRow => {
  if (index < 3) debugLog('[transformHourlyToRow] raw API item:', JSON.stringify(item))
  return {
    id: index + 1,
    meterNumber: item.meterNumber != null ? String(item.meterNumber) : fallbackMeterNumber,
    timestamp: item.date,
    dayOfWeek: getDayOfWeekLabel(item.date),
    consumption: toNumOrNull(item.consumption),
    value: toNumOrNull(item.consumed) as any,
  }
}

// Daily readings types - matches actual API response
export type DailyReadingApiItem = {
  meterId: number
  meterNumber: number | null
  day: string // Date in format "YYYY-MM-DD"
  dailyConsumption: number
  nightlyConsumption: number
  startReading: number
  // Legacy fields for backwards compatibility
  consumerId?: number
  date?: string
  consumption?: number
  lastConsumption?: number
  consumed?: number
}

// Min/Max detail from API with page info
export type MinMaxDetail = {
  day: string
  consumption: number
  page: {
    content: DailyReadingApiItem[]
    pageNumber: number
    totalPages: number
    totalElements: number
  }
}

export type DailyReadingsApiResponse = {
  success: boolean
  data: {
    collection: {
      total: number
      pageSize: number
      page: number
      result: DailyReadingApiItem[]
    }
    // New API fields for daily consumption stats
    totalDailyConsumption: number
    avgDailyConsumption: number
    totalNightlyConsumption: number
    avgNightlyConsumption: number
    // Min/Max with page info
    daily?: {
      min: MinMaxDetail
      max: MinMaxDetail
    }
    nightly?: {
      min: MinMaxDetail
      max: MinMaxDetail
    }
    // Legacy fields
    totalConsumption?: number
    avgConsumption?: number
    minDailyConsumption?: number
    maxDailyConsumption?: number
    minNightlyConsumption?: number
    maxNightlyConsumption?: number
  }
}

// Transformed row type for DataGrid
export type DailyReadingRow = {
  id: number
  meterNumber: string
  timestamp: string
  date: string
  midnightCapacity: number
  value: number
  consumption: number
  nightConsumption: number
  lastConsumption: number
  alerts: string[] | null
  dayOfWeek: string
}

// Transform API data to DataGrid row format
const transformDailyToRow = (item: DailyReadingApiItem, fallbackMeterNumber: string, index: number): DailyReadingRow => {
  if (index < 3) debugLog('[transformDailyToRow] raw API item:', JSON.stringify(item))
  const dailyConsumption = toNumOrNull(item.dailyConsumption) ?? toNumOrNull(item.consumption) ?? toNumOrNull(item.consumed)
  const nightlyConsumption = toNumOrNull(item.nightlyConsumption)
  const startReading = toNumOrNull(item.startReading) ?? toNumOrNull(item.lastConsumption)
  const dateValue = item.day || item.date || ''

  if (index < 3) debugLog('[transformDailyToRow] transformed:', { dailyConsumption, nightlyConsumption, startReading })

  return {
    id: index + 1,
    meterNumber: item.meterNumber != null ? String(item.meterNumber) : fallbackMeterNumber,
    timestamp: dateValue,
    dayOfWeek: getDayOfWeekLabel(dateValue),
    date: dateValue,
    midnightCapacity: startReading as any,
    value: startReading as any,
    consumption: dailyConsumption as any,
    nightConsumption: nightlyConsumption as any,
    lastConsumption: startReading as any,
    alerts: (item as any).alerts ?? null,
  }
}

export type DailyMinMax = {
  value: number
  day: string
  pageNumber: number
  totalPages: number
  totalElements: number
  pageRows: DailyReadingRow[] | null
}

export const READINGS_FETCH_FAILED = 'טעינת הקריאות נכשלה'

export type DailyReadingsPage = {
  rows: DailyReadingRow[]
  total: number
  page: number
  pageSize: number
  // Daily consumption stats
  totalConsumption: number
  avgConsumption: number
  // Min/Max with page info and pre-loaded rows for jump functionality
  dailyMin: DailyMinMax | null
  dailyMax: DailyMinMax | null
  nightlyMin: DailyMinMax | null
  nightlyMax: DailyMinMax | null
  // Nightly consumption stats
  totalNightlyConsumption: number
  avgNightlyConsumption: number
}

// Fetch daily readings for a consumer
export const getDailyReadings = async (
  accountId: number | string,
  premiseId: string | number,
  params: {
    page?: number
    size?: number
    from?: string
    to?: string
    sortBy?: string
    sortOrder?: 'ASC' | 'DESC'
    langKey?: string
    meterNumber?: string | number
  } = {}
): Promise<GatewayResult<DailyReadingsPage>> => {
  const { page = 1, size = 10, from, to, sortBy, sortOrder = 'DESC', langKey = 'he' } = params

  try {
    // Build query params
    const queryParams = new URLSearchParams()
    queryParams.append('page', page.toString())
    queryParams.append('size', size.toString())
    if (from) queryParams.append('from', from)
    if (to) queryParams.append('to', to)
    if (sortBy) queryParams.append('sortBy', sortBy)
    queryParams.append('sortOrder', sortOrder)
    queryParams.append('langKey', langKey)

    const res = await axios.post<DailyReadingsApiResponse>(
      `${API_BASE_URL}/api/consumer/${accountId}/${premiseId}/daily?${queryParams.toString()}`,
      {},
      {
        headers: { 'Content-Type': 'application/json' },
      }
    )

    debugLog('[consumerService] Daily API raw response:', JSON.stringify(res.data, null, 2))

    const collection = res.data?.data?.collection
    const resultArray = collection?.result ?? []
    const apiData = res.data?.data

    const rows = resultArray.map((item, index) => transformDailyToRow(item, premiseId.toString(), index))

    // Extract min/max with page info and pre-loaded page rows
    const buildMinMax = (entry: any) => {
      if (!entry) return null
      const pageContent = entry.page?.content
      return {
        value: entry.consumption,
        day: entry.day,
        pageNumber: entry.page?.pageNumber ?? 1,
        totalPages: entry.page?.totalPages ?? 1,
        totalElements: entry.page?.totalElements ?? 0,
        pageRows: Array.isArray(pageContent)
          ? pageContent.map((item: any, i: number) => transformDailyToRow(item, premiseId.toString(), i))
          : null,
      }
    }

    const dailyMin = buildMinMax(apiData?.daily?.min)
    const dailyMax = buildMinMax(apiData?.daily?.max)
    const nightlyMin = buildMinMax(apiData?.nightly?.min)
    const nightlyMax = buildMinMax(apiData?.nightly?.max)

    debugLog('[consumerService] Daily min/max:', { dailyMin, dailyMax, nightlyMin, nightlyMax })

    return {
      ok: true,
      data: {
        rows,
        total: collection?.total ?? rows.length,
        page: collection?.page ?? page,
        pageSize: collection?.pageSize ?? size,
        // Daily consumption stats
        totalConsumption: apiData?.totalDailyConsumption ?? apiData?.totalConsumption ?? 0,
        avgConsumption: apiData?.avgDailyConsumption ?? apiData?.avgConsumption ?? 0,
        // Min/Max with page info
        dailyMin,
        dailyMax,
        nightlyMin,
        nightlyMax,
        // Nightly consumption stats
        totalNightlyConsumption: apiData?.totalNightlyConsumption ?? 0,
        avgNightlyConsumption: apiData?.avgNightlyConsumption ?? 0,
      },
    }
  } catch (error) {
    console.error('[consumerService] Failed to fetch daily readings:', error)
    return { ok: false, error: toGatewayError(error, READINGS_FETCH_FAILED) }
  }
}

export type HourlyReadingsPage = {
  rows: HourlyReadingRow[]
  total: number
  page: number
  pageSize: number
  totalConsumption: number
  avgConsumption: number
}

// Fetch hourly readings for a consumer
export const getHourlyReadings = async (
  accountId: number | string,
  premiseId: string | number,
  params: {
    page?: number
    size?: number
    from?: string
    to?: string
    sortBy?: string
    sortOrder?: 'ASC' | 'DESC'
    langKey?: string
    meterNumber?: string | number
  } = {}
): Promise<GatewayResult<HourlyReadingsPage>> => {
  const { page = 1, size = 10, from, to, sortBy, sortOrder = 'DESC', langKey = 'he' } = params

  try {
    // Build query params
    const queryParams = new URLSearchParams()
    queryParams.append('page', page.toString())
    queryParams.append('size', size.toString())
    if (from) queryParams.append('from', from)
    if (to) queryParams.append('to', to)
    if (sortBy) queryParams.append('sortBy', sortBy)
    queryParams.append('sortOrder', sortOrder)
    queryParams.append('langKey', langKey)

    const res = await axios.post<HourlyReadingsApiResponse>(
      `${API_BASE_URL}/api/consumer/${accountId}/${premiseId}/hourly?${queryParams.toString()}`,
      {},
      {
        headers: { 'Content-Type': 'application/json' },
      }
    )

    const apiData = res.data?.data
    const collection = apiData?.collection
    const resultArray = collection?.result ?? []

    const rows = resultArray.map((item, index) => transformHourlyToRow(item, premiseId.toString(), index))

    return {
      ok: true,
      data: {
        rows,
        total: collection?.total ?? rows.length,
        page: collection?.page ?? page,
        pageSize: collection?.pageSize ?? size,
        totalConsumption: apiData?.totalConsumption ?? 0,
        avgConsumption: apiData?.avgConsumption ?? 0,
      },
    }
  } catch (error) {
    console.error('[consumerService] Failed to fetch hourly readings:', error)
    return { ok: false, error: toGatewayError(error, READINGS_FETCH_FAILED) }
  }
}

// ============ PERIODIC CONSUMPTION ============

export type PeriodOption = 'MONTHLY' | 'BI_MONTHLY' | 'QUARTERLY' | 'SEMI_ANNUAL' | 'ANNUAL'

export type PeriodicReadingApiItem = {
  meterId: number
  meterNumber: number | null
  periodStart: string
  periodEnd?: string | null
  consumption: number
}

export type PeriodicReadingsApiResponse = {
  success: boolean
  data: {
    collection: {
      total: number
      pageSize: number
      page: number
      result: PeriodicReadingApiItem[]
    }
    chart: {
      currentYear: number[]
      yearAgo: number[] | null
    }
    totalConsumption: number
    totalYearAgoConsumption: number | null
    periodOption: PeriodOption
    compareWithYearAgo: boolean
  }
  message: string
  error: string | null
  statusCode: number
}

export type PeriodicReadingRow = {
  id: number
  meterNumber: string
  period: string
  periodEnd?: string | null
  consumption: number
  alerts: string[] | null
  replacementDates: string[] | null
  dayOfWeek: string
}

// Transform API periodic item to row format
const transformPeriodicToRow = (item: PeriodicReadingApiItem, fallbackMeterNumber: string, index: number): PeriodicReadingRow => {
  if (index < 3) debugLog('[transformPeriodicToRow] raw API item:', JSON.stringify(item))
  return {
    id: index + 1,
    meterNumber: item.meterNumber != null ? String(item.meterNumber) : fallbackMeterNumber,
    period: item.periodStart,
    periodEnd: item.periodEnd ?? null,
    dayOfWeek: getDayOfWeekLabel(item.periodStart),
    consumption: toNumOrNull(item.consumption) as any,
    alerts: (item as any).alerts ?? null,
    replacementDates: (item as any).replacementDates ?? null,
  }
}

// Fetch periodic readings for a consumer
export const getPeriodicReadings = async (
  accountId: number | string,
  premiseId: string | number,
  params: {
    page?: number
    size?: number
    from?: string
    to?: string
    periodOption?: PeriodOption
    sortBy?: string
    sortOrder?: 'ASC' | 'DESC'
    langKey?: string
    meterNumber?: string | number
  } = {}
): Promise<{
  rows: PeriodicReadingRow[]
  total: number
  page: number
  pageSize: number
  totalConsumption: number
  totalYearAgoConsumption: number | null
  chart: {
    currentYear: number[]
    yearAgo: number[] | null
  }
  periodOption: PeriodOption
}> => {
  const { page = 1, size = 100, from, to, periodOption = 'MONTHLY', sortBy, sortOrder = 'DESC', langKey = 'he', meterNumber } = params

  debugLog('[consumerService] Fetching periodic readings:', { accountId, premiseId, params })

  try {
    // Build query params
    const queryParams = new URLSearchParams()
    queryParams.append('page', page.toString())
    queryParams.append('size', size.toString())
    if (from) queryParams.append('from', from)
    if (to) queryParams.append('to', to)
    queryParams.append('periodOption', periodOption)
    if (sortBy) queryParams.append('sortBy', sortBy)
    queryParams.append('sortOrder', sortOrder)
    queryParams.append('langKey', langKey)
    if (meterNumber !== undefined && meterNumber !== null) {
      queryParams.append('meterNumber', String(meterNumber))
    }

    const apiUrl = `${API_BASE_URL}/api/consumer/${accountId}/${premiseId}/period?${queryParams.toString()}`
    debugLog('[consumerService] Periodic API URL:', apiUrl)
    
    const res = await axios.post<PeriodicReadingsApiResponse>(
      apiUrl,
      {},
      {
        headers: { 'Content-Type': 'application/json' },
      }
    )

    // Some backend endpoints return an already serialized JSON string. Parse
    // it here as well so a valid response is not mistaken for an empty one.
    let responseBody: any = res.data
    while (typeof responseBody === 'string') {
      try {
        responseBody = JSON.parse(responseBody)
      } catch {
        break
      }
    }

    const apiData = responseBody?.data
    const collection = apiData?.collection
    const resultArray: PeriodicReadingApiItem[] = Array.isArray(collection?.result)
      ? collection.result
      : Array.isArray((collection as any)?.content)
        ? (collection as any).content
        : []

    if (import.meta.env.DEV) {
      debugLog('HTTP status:', res.status)
      debugLog('Request URL:', apiUrl)
      debugLog('Response flags:', {
        success: responseBody?.success,
        statusCode: responseBody?.statusCode,
        message: responseBody?.message,
        error: responseBody?.error,
      })
      debugLog('Collection:', collection)
      debugLog('Rows received:', resultArray.length)
      debugLog('First rows:', resultArray.slice(0, 5))
      debugLog('Chart:', apiData?.chart)

      if (!collection) {
        console.warn(
          '[PERIODIC DEBUG] Response has no data.collection. Raw response:',
          responseBody
        )
      } else if (resultArray.length === 0) {
        console.warn(
          '[PERIODIC DEBUG] The backend returned an empty periodic collection.'
        )
      }
    }

    const rows = resultArray.map((item, index) =>
      transformPeriodicToRow(item, String(params.meterNumber ?? premiseId), index)
    )

    return {
      rows,
      total: collection?.total ?? rows.length,
      page: collection?.page ?? page,
      pageSize: collection?.pageSize ?? size,
      totalConsumption: apiData?.totalConsumption ?? 0,
      totalYearAgoConsumption: apiData?.totalYearAgoConsumption ?? null,
      chart: {
        currentYear: apiData?.chart?.currentYear ?? [],
        yearAgo: apiData?.chart?.yearAgo ?? null,
      },
      periodOption: apiData?.periodOption ?? periodOption,
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error('[PERIODIC DEBUG] Periodic API request failed:', {
        status: error.response?.status,
        url: error.config?.url,
        response: error.response?.data,
        message: error.message,
      })
    } else {
      console.error('[PERIODIC DEBUG] Periodic API request failed:', error)
    }
    return {
      rows: [],
      total: 0,
      page,
      pageSize: size,
      totalConsumption: 0,
      totalYearAgoConsumption: null,
      chart: {
        currentYear: [],
        yearAgo: null,
      },
      periodOption,
    }
  }
}

// ============ DETAILED CONSUMPTION ============

export type DetailedReadingApiItem = {
  meterId: number
  meterNumber: number | null
  readingAt: string
  value: number
  reverse: number
}

export type DetailedReadingsApiResponse = {
  success: boolean
  data: {
    collection: {
      total: number
      pageSize: number
      page: number
      result: DetailedReadingApiItem[]
    }
  }
  message?: string
  error?: string
  statusCode?: number
}

export type DetailedReadingRow = {
  id: number
  meterNumber: string
  timestamp: string
  value: number
  consumption: number // reverse value
  dayOfWeek: string
}

const transformDetailedToRow = (item: DetailedReadingApiItem, fallbackMeterNumber: string, index: number): DetailedReadingRow => {
  if (index < 3) debugLog('[transformDetailedToRow] raw API item:', JSON.stringify(item))
  return {
    id: index + 1,
    meterNumber: item.meterNumber != null ? String(item.meterNumber) : fallbackMeterNumber,
    timestamp: item.readingAt,
    dayOfWeek: getDayOfWeekLabel(item.readingAt),
    value: toNumOrNull(item.value) as any,
    consumption: toNumOrNull(item.reverse) as any,
  }
}

export const getDetailedReadings = async (
  accountId: number | string,
  premiseId: string | number,
  params: {
    page?: number
    size?: number
    from?: string
    to?: string
    sortBy?: string
    sortOrder?: 'ASC' | 'DESC'
    langKey?: string
  } = {}
): Promise<{
  rows: DetailedReadingRow[]
  total: number
  page: number
  pageSize: number
}> => {
  const { page = 1, size = 100, from, to, sortBy, sortOrder = 'DESC', langKey = 'he' } = params

  debugLog('[consumerService] Fetching detailed readings:', { accountId, premiseId, params })

  try {
    // Build query params
    const queryParams = new URLSearchParams()
    queryParams.append('page', page.toString())
    queryParams.append('size', size.toString())
    if (from) queryParams.append('from', from)
    if (to) queryParams.append('to', to)
    if (sortBy) queryParams.append('sortBy', sortBy)
    queryParams.append('sortOrder', sortOrder)
    queryParams.append('langKey', langKey)

    const apiUrl = `${API_BASE_URL}/api/consumer/${accountId}/${premiseId}/detailed?${queryParams.toString()}`
    debugLog('[consumerService] Detailed API URL:', apiUrl)

    const res = await axios.post<DetailedReadingsApiResponse>(
      apiUrl,
      {},
      {
        headers: { 'Content-Type': 'application/json' },
      }
    )

    debugLog('[consumerService] Detailed readings response:', res.data)

    const apiData = res.data?.data
    const collection = apiData?.collection
    const resultArray = collection?.result ?? []

    const rows = resultArray.map((item, index) => transformDetailedToRow(item, premiseId.toString(), index))

    return {
      rows,
      total: collection?.total ?? rows.length,
      page: collection?.page ?? page,
      pageSize: collection?.pageSize ?? size,
    }
  } catch (error) {
    console.error('[consumerService] Failed to fetch detailed readings:', error)
    return {
      rows: [],
      total: 0,
      page,
      pageSize: size,
    }
  }
}

// =============================================================================
// Server-side Excel Export
// =============================================================================

/**
 * Download consumers Excel file generated by the backend.
 * POST /api/consumer/{accountId}/export
 */
export const exportConsumersExcel = async (
  accountId: number | string,
  filters: ApiResourceFilter[] = [],
  langKey: string = 'he',
  sortBy: string = 'premiseId',
  sortOrder: 'ASC' | 'DESC' = 'DESC',
  onProgress?: (percent: number) => void
): Promise<void> => {
  const exportBody: { apiResources: ApiResourceFilter[]; logicalOperator: string } = { apiResources: filters, logicalOperator: 'AND' }
  const url = `${API_BASE_URL}/api/consumer/${accountId}/export?sortBy=${sortBy}&sortOrder=${sortOrder}&langKey=${langKey}`
  debugLog('[exportConsumersExcel] Starting export:', url, 'body:', exportBody)
  await downloadExcelBlob(url, 'consumers.xlsx', onProgress, exportBody)
}

/**
 * Generic helper to download an Excel export from a backend endpoint.
 */
export const downloadExcelBlob = (url: string, defaultFilename: string, onProgress?: (percent: number) => void, body: any = {}) =>
  downloadBlob(url, defaultFilename, { body, onProgress })

/**
 * Export hourly readings to Excel.
 * POST /api/consumer/{accountId}/{premiseId}/hourly/export
 */
export const exportHourlyReadingsExcel = async (
  accountId: number | string,
  premiseId: number | string,
  params: { from?: string; to?: string; sortBy?: string; sortOrder?: string; langKey?: string },
  onProgress?: (percent: number) => void
): Promise<void> => {
  const q = new URLSearchParams()
  if (params.from) q.append('from', params.from)
  if (params.to) q.append('to', params.to)
  if (params.sortBy) q.append('sortBy', params.sortBy)
  if (params.sortOrder) q.append('sortOrder', params.sortOrder)
  q.append('langKey', params.langKey ?? 'he')
  await downloadExcelBlob(`${API_BASE_URL}/api/consumer/${accountId}/${premiseId}/hourly/export?${q}`, 'hourly-readings.xlsx', onProgress)
}

/**
 * Export daily readings to Excel.
 * POST /api/consumer/{accountId}/{premiseId}/daily/export
 */
export const exportDailyReadingsExcel = async (
  accountId: number | string,
  premiseId: number | string,
  params: { from?: string; to?: string; sortBy?: string; sortOrder?: string; langKey?: string },
  onProgress?: (percent: number) => void
): Promise<void> => {
  const q = new URLSearchParams()
  if (params.from) q.append('from', params.from)
  if (params.to) q.append('to', params.to)
  if (params.sortBy) q.append('sortBy', params.sortBy)
  if (params.sortOrder) q.append('sortOrder', params.sortOrder)
  q.append('langKey', params.langKey ?? 'he')
  await downloadExcelBlob(`${API_BASE_URL}/api/consumer/${accountId}/${premiseId}/daily/export?${q}`, 'daily-readings.xlsx', onProgress)
}

/**
 * Export periodic readings to Excel.
 * POST /api/consumer/{accountId}/{premiseId}/period/export
 */
export const exportPeriodicReadingsExcel = async (
  accountId: number | string,
  premiseId: number | string,
  params: { from?: string; to?: string; sortBy?: string; sortOrder?: string; periodOption?: string; langKey?: string },
  onProgress?: (percent: number) => void
): Promise<void> => {
  const q = new URLSearchParams()
  if (params.from) q.append('from', params.from)
  if (params.to) q.append('to', params.to)
  if (params.sortBy) q.append('sortBy', params.sortBy)
  if (params.sortOrder) q.append('sortOrder', params.sortOrder)
  if (params.periodOption) q.append('periodOption', params.periodOption)
  q.append('langKey', params.langKey ?? 'he')
  await downloadExcelBlob(`${API_BASE_URL}/api/consumer/${accountId}/${premiseId}/period/export?${q}`, 'period-readings.xlsx', onProgress)
}

/**
 * Export detailed readings to Excel.
 * POST /api/consumer/{accountId}/{premiseId}/detailed/export
 */
export const exportDetailedReadingsExcel = async (
  accountId: number | string,
  premiseId: number | string,
  params: { from?: string; to?: string; sortBy?: string; sortOrder?: string; langKey?: string },
  onProgress?: (percent: number) => void
): Promise<void> => {
  const q = new URLSearchParams()
  if (params.from) q.append('from', params.from)
  if (params.to) q.append('to', params.to)
  if (params.sortBy) q.append('sortBy', params.sortBy)
  if (params.sortOrder) q.append('sortOrder', params.sortOrder)
  q.append('langKey', params.langKey ?? 'he')
  await downloadExcelBlob(`${API_BASE_URL}/api/consumer/${accountId}/${premiseId}/detailed/export?${q}`, 'detailed-readings.xlsx', onProgress)
}

// =============================================================================
// Map Clustering API
// =============================================================================

// One server grid cell: cellX/cellY are the cell's node index on the grid of
// 360 / 2^(zoom + 3) degrees. The premise fields are set only for a cell of one point.
export interface ClusterMarker {
  pointCount: number
  longitude: number
  latitude: number
  cellX: number
  cellY: number
  premiseId?: number | null
  payerName?: string | null
  address?: string | null
  meterNumber?: number | null
}

// The live part of a cell, never cached. It names the same cells as the
// clusters for the same box, zoom and filters. alertCount is how many of the
// cell's pointCount points have an open alert.
export interface ClusterStatus {
  cellX: number
  cellY: number
  pointCount: number
  alertCount: number
  lastReadingAt?: string | null
}

export interface MapBounds {
  xmin: number
  ymin: number
  xmax: number
  ymax: number
}

type MapFilters = { name: string; value: string[] }[]

const postMapCells = async <T>(
  path: string,
  bounds: MapBounds,
  zoom: number,
  filters?: MapFilters,
  signal?: AbortSignal
): Promise<T[]> => {
  const queryParams = new URLSearchParams({
    xmin: bounds.xmin.toString(),
    ymin: bounds.ymin.toString(),
    xmax: bounds.xmax.toString(),
    ymax: bounds.ymax.toString(),
    zoom: zoom.toString(),
  })
  const body = filters && filters.length > 0 ? { apiResources: filters } : {}
  const res = await axios.post<{ success: boolean; data: T[] }>(
    `${API_BASE_URL}${path}?${queryParams.toString()}`,
    body,
    { headers: { 'Content-Type': 'application/json' }, signal }
  )
  return res.data?.data ?? []
}

/**
 * Fetch clustered markers from the server based on viewport bounds and zoom level.
 * Returns clusters when zoomed out, individual markers when zoomed in.
 * Rejects on a failed request: the caller has to tell "no markers here" from
 * "the request failed", because the map says which of the two happened.
 */
export const getClusteredMarkers = (
  accountId: string | number,
  bounds: MapBounds,
  zoom: number,
  filters?: MapFilters,
  signal?: AbortSignal
): Promise<ClusterMarker[]> =>
  postMapCells(
    `/api/consumer/${accountId}/map/markers`,
    bounds,
    zoom,
    filters,
    signal
  )

/** Fetch the point and alert counts and last reading time for the cells of a box. */
export const getClusteredMarkerStatus = (
  accountId: string | number,
  bounds: MapBounds,
  zoom: number,
  filters?: MapFilters,
  signal?: AbortSignal
): Promise<ClusterStatus[]> =>
  postMapCells(
    `/api/consumer/${accountId}/map/markers/status`,
    bounds,
    zoom,
    filters,
    signal
  )

// =============================================================================
// Notifications API
// =============================================================================

export interface NotificationTypeCount {
  notificationTypeId: number
  notificationTypeName: string
  count: number
}

/**
 * Fetch open notification counts grouped by type.
 */
export const getOpenNotifications = async (
  accountId: string | number,
  page: number = 1,
  size: number = 500
): Promise<NotificationTypeCount[]> => {
  try {
    const res = await axios.get<{
      success: boolean
      data: {
        rValue: number
        payerId: number
        sensorStatus: boolean
        total: number
        pageSize: number
        page: number
        result: NotificationTypeCount[]
      }
    }>(`${API_BASE_URL}/api/notifications/${accountId}/open`, {
      params: { page, size },
      headers: { 'Content-Type': 'application/json' },
    })

    return res.data?.data?.result ?? []
  } catch (error) {
    console.error('[consumerService] Failed to fetch notifications:', error)
    return []
  }
}

// Selected Meter Readings report
export const getSelectedMeterReadings = async (params: {
  accountId: number | string
  from: string
  to: string
  filterType: 'meterNumbers' | 'waterCards'
  values: string[]
  page?: number
  size?: number
  sortBy?: string
  sortOrder?: 'ASC' | 'DESC'
  langKey?: string
  apiFilter?: { apiResources: { name: string; value: string[]; operator?: string }[]; logicalOperator?: 'AND' | 'OR' }
}): Promise<{ result: any[]; total: number; page: number; pageSize: number }> => {
  const { accountId, from, to, filterType, values, page = 1, size = 50, sortBy = 'billing_id', sortOrder = 'DESC', langKey = 'he', apiFilter } = params
  try {
    const res = await axios.post(
      `${API_BASE_URL}/api/reports/${accountId}/selected-meter-readings?from=${from}&to=${to}&page=${page}&size=${size}&sortBy=${sortBy}&sortOrder=${sortOrder}&langKey=${langKey}`,
      { filterType, values, apiFilter: apiFilter || null },
      { headers: { 'Content-Type': 'application/json' } }
    )
    const data = res.data?.data
    return {
      result: data?.result ?? [],
      total: data?.total ?? 0,
      page: data?.page ?? page,
      pageSize: data?.pageSize ?? size,
    }
  } catch (error) {
    console.error('[consumerService] Failed to fetch selected meter readings:', error)
    return { result: [], total: 0, page, pageSize: size }
  }
}

export const exportSelectedMeterReadings = async (params: {
  accountId: number | string
  from: string
  to: string
  filterType: 'meterNumbers' | 'waterCards'
  values: string[]
  sortBy?: string
  sortOrder?: 'ASC' | 'DESC'
  langKey?: string
}): Promise<void> => {
  const { accountId, from, to, filterType, values, sortBy = 'billing_id', sortOrder = 'DESC', langKey = 'he' } = params
  try {
    await downloadBlob(
      `${API_BASE_URL}/api/reports/${accountId}/selected-meter-readings/export?from=${from}&to=${to}&sortBy=${sortBy}&sortOrder=${sortOrder}&langKey=${langKey}`,
      'selected-meter-readings.xlsx',
      { body: { filterType, values, apiFilter: null } }
    )
  } catch (error) {
    console.error('[consumerService] Failed to export selected meter readings:', error)
  }
}
